/*
 *   File : cron-history.js
 *   Author : https://evoqins.com
 *   Description : Cron history
 *   Integrations : NA
 *   Version : 1.0.0
*/

import { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import color from '../../../Style/color.scss';

// custom components
import { EmptyScreen, Loader, Chip } from '../../../Components/Others';
import { DateRangePicker } from '../../../Components/FormElements';
import { Icon } from '../../../Components/Icon';
import { PrimaryButton } from '../../../Components/Buttons';
import { CustomDataTable } from '../../../Components/DataTable';
import { APIService } from '../../../Service';
import { format } from 'date-fns';

const ITEMS_PER_PAGE = 25;
const OPS_VIEW_APIS_SESSION_KEY = 'ops_view_apis_restore';

const ApiHistory = () => {

    const navigate = useNavigate();
    const scrollRef = useRef(null);
    const { state } = useLocation();
    const [selectedDate, setSelectedDate] = useState(null);
    const [pageNum, setPageNum] = useState(1);
    const [loader, setLoader] = useState(true);
    const [filterToggle, setFilterToggle] = useState(false);
    const [listData, setListData] = useState({});

    const actionRef = useRef(null);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    }, []);

    // handle navigate detail
    const _handleDetail = (customer_id) => {
        // Leaving api-history for Customer — do not restore View APIs modal later
        try {
            sessionStorage.removeItem(OPS_VIEW_APIS_SESSION_KEY);
        } catch (_) {
            /* ignore */
        }
        navigate(`/customers/${customer_id}`, {
            state: {
                source: "normal_listing",
            }
        });
    };

    // Table column
    const TABLE_COLUMN = [
        {
            name: 'Customer ID',
            selector: row => row.customer_id || "-",
            cell: (row) => 
            row.customer_id ? <span className='e-text-blue-violet text-truncate text-decoration-hover-underline cursor-pointer'
                onClick={() => _handleDetail(row.customer_id)}>
                {row.rayi_customer_id || "-"}
            </span> : "-",
            width: "120px",
            sortable: true,
        },
        {
            name: 'Id',
            cell: row => <>{row.id}</>,
            width: "120px",
        },
        {
            name: 'Success/ Failed',
            cell: row => (
                <Chip
                    label={row.api_status}
                    className={row.api_status === "Success" ? "e-bg-green-crayola" : "e-bg-maximum-red"}
                />
            ),
            width: "160px",
        },
        {
            name: 'Failed reason',
            cell: row =>
                <div>
                    {(row.api_status === "Success" || row?.failure_data === null)
                        ? '-'
                        : 
                            <span  className='me-1'>{row.failure_data}</span>
                        
                    }
                </div>,
            width: "380px"
        },
        {
            name: 'Timestamp',
            selector: row => row.created_at || "-",
            width: "220px"
        }
    ];

    // Fetch once per api id. Deferred so React StrictMode's mount→cleanup→remount
    // does not fire the API twice (first scheduled call is cleared in cleanup).
    useEffect(() => {
        if (!state?.id) {
            navigate("/ops", {
                state: {
                    tab_index: 2
                }
            });
            return;
        }

        const timeoutId = setTimeout(() => {
            _getHistory(state.id);
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [state?.id]);

    useEffect(() => {
        if (!actionRef.current) return;

        if (actionRef.current === "clear") {
            _getHistory(state?.id, 1);
            setPageNum(1);
        }

        actionRef.current = null;
    }, [selectedDate]);

    // handle filter toggle
    const _handleFilterToggle = () => {
        setFilterToggle(prev => !prev);
    };

    // handle table page change 
    const _handlePageChange = (page_num) => {
        setPageNum(page_num);
        _getHistory(state?.id, page_num);

        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    };

    //API function 
    function _getHistory(id = state?.id, page = pageNum) {
        setLoader(true);

        const start_date =
            selectedDate === null ? null : format(new Date(selectedDate[0]), 'dd-MM-yyyy');
        const end_date =
            selectedDate === null ? null : format(new Date(selectedDate[1]), 'dd-MM-yyyy');

        const payload = {
            api_id: id,
            page_num: page,
            page_size: ITEMS_PER_PAGE,
            start_date,
            end_date
        };

        APIService(true, 'ops-dashboard/api-status-detail', payload).then((response) => {
            if (response?.status_code === 200) {
                setListData(response.data);
            } else {
                setListData({ data: [] });
            }
            setLoader(false);
        });
    }

    return (
        <section className='e-section-height pb-5'>
            <div className='e-bg-lavender padding-52px-lr pt-2 padding-112px-bottom'>
                {/* <p className='mb-0 e-text-chinese-black e-primary-font e-font-24 e-font-weight-600 line-height-24px'>
                    Cron status history
                </p> */}

                <p className='mb-0 e-text-chinese-black e-primary-font e-font-16 e-font-weight-400 line-height-24px'>
                    <span className='text-decoration-hover-underline cursor-pointer'
                        onClick={() => navigate("/ops",
                            {
                                state: {
                                    ...state,
                                    tab_index: state?.tab_index,
                                    api_data: state?.api_data
                                }
                            }
                        )}>
                        <Icon icon="back"
                            size={20}
                            className="margin-10px-right"
                            color="#292D32" />
                        OPS Dashboard </span> / <span className='e-font-weight-600 text-capitalize'>
                        {listData?.api_name?.split("_").join(" ") ?? "-"}
                    </span>
                </p>

            </div>

            {/* Table section */}
            <div className='padding-52px-lr margin--80px-top'>
                <div className='e-bg-ghost-white border-radius-16 e-box-shadow-ops-wrapper' ref={scrollRef}>
                    <div className='padding-32px-tb'>
                        <div className='padding-32px-lr d-flex align-items-center justify-content-end pb-3 gap-16px'>
                            <div
                                className={`padding-10px-all border-radius-8 e-cursor-pointer e-border-1px-light-purple cursor-pointer ${filterToggle ? 'e-bg-blue-violet' : ''}`}
                                onClick={_handleFilterToggle}
                            >
                                <Icon
                                    icon='filter'
                                    size='1.25rem'
                                    className='d-flex'
                                    color={filterToggle && color.white}
                                />
                            </div>
                        </div>

                        {/* Filter section */}
                        <div className={`e-filter-content ${filterToggle ? 'expanded' : 'e-overflow collapsed'}`}>
                            <div className='e-bg-main-white pt-3 pb-4 padding-32px-lr'>

                                <div className='row padding-32px-bottom'>
                                    <div className='col-3'>
                                        <DateRangePicker
                                            label="Choose date"
                                            placeholder="Select"
                                            width="w-100"
                                            maxDate={new Date}
                                            value={selectedDate}
                                            handleClear={() => {
                                                actionRef.current = "clear";
                                                setSelectedDate(null);
                                            }}
                                            handleChange={(date) => setSelectedDate(date)}
                                        />
                                    </div>
                                </div>

                                <PrimaryButton
                                    name='Filter now'
                                    paddingBlock='padding-12px-tb padding-40px-lr'
                                    onClick={() => {
                                        actionRef.current = "filter";
                                        _getHistory(state?.id, 1);
                                        setPageNum(1);
                                    }}
                                />
                            </div>
                        </div>

                        {loader ? (
                            <Loader className="py-5 min-vh-100" />
                        ) : listData?.data?.length !== 0 ? (
                            <div className='e-data-table-wrapper-default-updated pt-3'>
                                <CustomDataTable
                                    columns={TABLE_COLUMN}
                                    data={listData.data}
                                    pagination={true}
                                    paginationPerPage={ITEMS_PER_PAGE}
                                    paginationDefaultPage={listData.current_page}
                                    paginationTotalRows={listData.total_count}
                                    lastPage={listData.total_page}
                                    onChangePage={_handlePageChange}
                                />
                            </div>
                        ) : (
                            <EmptyScreen
                                image={require('../../../Assets/Image/NotFound/no-data.png')}
                                title='No data found'
                            />
                        )}

                    </div>
                </div>
            </div>
        </section>
    );
};

export default ApiHistory;
